import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { IconChevronRight, IconClose, IconPlus, IconSearch } from '@/components/Icons';
import {
  FILTER_CONDITIONS,
  activeFilters,
  newFilterRow,
  type ReportFilterRow,
} from '@/features/reports/reportFilters';
import type { UseReportLayout } from '@/features/reports/useReportLayout';

/**
 * The toolbar every report page renders above its table: free-text search,
 * a Filters row-builder, a Display-options column picker, and a date-range
 * picker with presets.
 *
 * Search and Filters are lifted to the caller (and from there into the API
 * query) rather than applied here — the server-paginated pages must filter
 * in the query, not on the fetched page.
 */

// ── Popover shell ─────────────────────────────────────────────────────

/**
 * A button that toggles a panel anchored beneath it. Closes on Escape and
 * on a click outside, and marks the trigger with `aria-expanded` so the
 * state is announced rather than only visible.
 */
function Popover({
  label,
  icon,
  active = false,
  children,
}: {
  label: ReactNode;
  icon?: ReactNode;
  active?: boolean;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onDown = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((value) => !value)}
        className={`btn btn-md ${
          active ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'btn-secondary'
        }`}
      >
        {icon}
        {label}
      </button>
      {open ? (
        <div
          id={panelId}
          className="absolute left-0 z-30 mt-2 rounded-lg border border-slate-200 bg-white p-4 shadow-lg"
        >
          {children(() => setOpen(false))}
        </div>
      ) : null}
    </div>
  );
}

// ── Search ────────────────────────────────────────────────────────────

export function ReportSearchField({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
}) {
  const id = useId();
  return (
    <div className="relative">
      <label className="sr-only" htmlFor={id}>
        {placeholder}
      </label>
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
        <IconSearch />
      </span>
      <input
        id={id}
        type="search"
        className="input w-72 pl-9"
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

// ── Filters ───────────────────────────────────────────────────────────

export interface ReportFiltersButtonProps {
  /** Filterable field names this page offers. */
  fields: readonly string[];
  /** The rows currently applied (the page owns these). */
  applied: readonly ReportFilterRow[];
  matchAny: boolean;
  /** Fired only on Apply / Clear — never per keystroke. */
  onApply: (rows: ReportFilterRow[], matchAny: boolean) => void;
  placeholderFor?: (field: string) => string;
}

/**
 * Edits a DRAFT copy of the rows; nothing reaches the page (and so no
 * refetch happens) until Apply is pressed. Typing a value must not make
 * the table flicker — and on a server-paginated list, must not fire a
 * request per keystroke.
 */
export function ReportFiltersButton({
  fields,
  applied,
  matchAny,
  onApply,
  placeholderFor,
}: ReportFiltersButtonProps) {
  const [draft, setDraft] = useState<ReportFilterRow[]>(() => applied.map((row) => ({ ...row })));
  const [draftMatchAny, setDraftMatchAny] = useState(matchAny);
  const appliedCount = activeFilters(applied).length;

  // Re-seed the draft whenever what's applied changes (including a reset
  // from the date picker), so re-opening never shows an abandoned edit.
  useEffect(() => {
    setDraft(applied.map((row) => ({ ...row })));
    setDraftMatchAny(matchAny);
  }, [applied, matchAny]);

  const update = (index: number, patch: Partial<ReportFilterRow>) => {
    setDraft((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  return (
    <Popover
      active={appliedCount > 0}
      label={appliedCount > 0 ? `Filters (${appliedCount})` : 'Filters'}
    >
      {(close) => (
        <div className="w-[36rem]">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <h2 className="text-sm font-semibold text-slate-900">Filters</h2>
              <button
                type="button"
                onClick={() => setDraftMatchAny((value) => !value)}
                className="rounded-full bg-brand-600/10 px-2.5 py-1 text-xs font-medium text-brand-700"
              >
                {draftMatchAny ? 'Match any (OR)' : 'Match all (AND)'}
              </button>
            </div>
            <div className="flex items-center gap-1">
              {draft.length > 0 ? (
                <button
                  type="button"
                  className="text-xs font-semibold text-red-600 hover:underline"
                  onClick={() => {
                    setDraft([]);
                    onApply([], draftMatchAny);
                  }}
                >
                  Clear filter
                </button>
              ) : null}
              <button
                type="button"
                aria-label="Close filters"
                className="rounded p-1 text-red-600 hover:bg-red-50"
                onClick={close}
              >
                <IconClose />
              </button>
            </div>
          </div>

          {draft.map((row, index) => (
            <div key={index} className="mb-2 flex items-center gap-2">
              <select
                aria-label={`Filter ${index + 1} field`}
                className="input flex-1"
                value={row.field}
                onChange={(event) => update(index, { field: event.target.value })}
              >
                {fields.map((field) => (
                  <option key={field} value={field}>
                    {field}
                  </option>
                ))}
              </select>
              <select
                aria-label={`Filter ${index + 1} condition`}
                className="input flex-1"
                value={row.condition}
                onChange={(event) =>
                  update(index, { condition: event.target.value as ReportFilterRow['condition'] })
                }
              >
                {FILTER_CONDITIONS.map((condition) => (
                  <option key={condition} value={condition}>
                    {condition}
                  </option>
                ))}
              </select>
              <input
                aria-label={`Filter ${index + 1} value`}
                className="input flex-1"
                placeholder={placeholderFor?.(row.field) ?? 'value'}
                value={row.value}
                onChange={(event) => update(index, { value: event.target.value })}
              />
              <button
                type="button"
                aria-label={`Remove filter ${index + 1}`}
                className="rounded p-1.5 text-red-600 hover:bg-red-50"
                onClick={() => setDraft((rows) => rows.filter((_, i) => i !== index))}
              >
                <IconClose />
              </button>
            </div>
          ))}

          <button
            type="button"
            className="btn btn-sm btn-ghost text-brand-700"
            onClick={() => setDraft((rows) => [...rows, newFilterRow(fields[0])])}
          >
            <IconPlus />
            Add filter
          </button>

          <div className="mt-3 flex justify-end border-t border-slate-200 pt-3">
            <button
              type="button"
              className="btn btn-md btn-primary"
              onClick={() => {
                onApply(
                  draft.map((row) => ({ ...row })),
                  draftMatchAny,
                );
                close();
              }}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </Popover>
  );
}

// ── Display options ───────────────────────────────────────────────────

export interface ReportColumnsButtonProps {
  layout: UseReportLayout;
  /** Toggleable columns as key → header label. */
  labels: Readonly<Record<string, string>>;
}

/** Show/hide a column, and move it in the order. */
export function ReportColumnsButton({ layout, labels }: ReportColumnsButtonProps) {
  const keys = layout.orderedKeys.filter((key) => labels[key] !== undefined);

  return (
    <Popover label="Display options">
      {() => (
        <div className="w-80">
          <ul className="max-h-80 overflow-y-auto">
            {keys.map((key, index) => (
              <li key={key} className="flex items-center gap-2 py-1">
                <span className="flex flex-col">
                  <button
                    type="button"
                    aria-label={`Move ${labels[key]} up`}
                    disabled={index === 0}
                    className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                    onClick={() => layout.move(key, index - 1)}
                  >
                    <span className="block rotate-[-90deg]">
                      <IconChevronRight />
                    </span>
                  </button>
                  <button
                    type="button"
                    aria-label={`Move ${labels[key]} down`}
                    disabled={index === keys.length - 1}
                    className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                    onClick={() => layout.move(key, index + 1)}
                  >
                    <span className="block rotate-90">
                      <IconChevronRight />
                    </span>
                  </button>
                </span>
                <label className="flex flex-1 items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300"
                    checked={layout.isVisible(key)}
                    onChange={(event) => layout.setHidden(key, !event.target.checked)}
                  />
                  {labels[key]}
                </label>
              </li>
            ))}
          </ul>
          <div className="mt-2 flex justify-between border-t border-slate-200 pt-2">
            <button
              type="button"
              className="text-xs font-medium text-brand-700 hover:underline"
              onClick={() => layout.setAllHidden(keys, true)}
            >
              Hide all
            </button>
            <button
              type="button"
              className="text-xs font-medium text-brand-700 hover:underline"
              onClick={() => layout.setAllHidden(keys, false)}
            >
              Show all
            </button>
          </div>
        </div>
      )}
    </Popover>
  );
}

// ── Period ────────────────────────────────────────────────────────────

export const PERIOD_PRESETS = ['Today', 'This Week', 'This Month', 'Last 3 Months', 'This Year'] as const;

export type PeriodPreset = (typeof PERIOD_PRESETS)[number];

function iso(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Resolve a preset to concrete from/to dates in the browser's timezone. */
export function resolvePreset(preset: PeriodPreset): { from: string; to: string } {
  const now = new Date();
  const today = iso(now);
  switch (preset) {
    case 'Today':
      return { from: today, to: today };
    case 'This Week': {
      const day = (now.getDay() + 6) % 7; // Monday-based
      const start = new Date(now);
      start.setDate(now.getDate() - day);
      return { from: iso(start), to: today };
    }
    case 'This Month':
      return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: today };
    case 'Last 3 Months':
      return { from: iso(new Date(now.getFullYear(), now.getMonth() - 2, 1)), to: today };
    case 'This Year':
      return { from: iso(new Date(now.getFullYear(), 0, 1)), to: today };
  }
}

export interface ReportPeriodButtonProps {
  from: string;
  to: string;
  onChange: (next: { from: string; to: string }) => void;
  /** Greyed because a filter, not this range, is driving the table. */
  deselected?: boolean;
}

/** Presets down the left, explicit From/To on the right. */
export function ReportPeriodButton({ from, to, onChange, deselected = false }: ReportPeriodButtonProps) {
  const label = deselected ? 'Filtered results' : from && to ? `${from} → ${to}` : 'Date range';

  return (
    <Popover
      label={<span className={deselected ? 'italic text-slate-400' : undefined}>{label}</span>}
    >
      {() => (
        <div className="flex w-[26rem] gap-4">
          <ul className="w-40 shrink-0 border-r border-slate-200 pr-2">
            {PERIOD_PRESETS.map((preset) => (
              <li key={preset}>
                <button
                  type="button"
                  className="w-full rounded px-2 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-100"
                  onClick={() => onChange(resolvePreset(preset))}
                >
                  {preset}
                </button>
              </li>
            ))}
          </ul>
          <div className="flex-1 space-y-3">
            <div>
              <label className="label" htmlFor="report-period-from">
                From
              </label>
              <input
                id="report-period-from"
                type="date"
                className="input"
                value={from}
                onChange={(event) => onChange({ from: event.target.value, to })}
              />
            </div>
            <div>
              <label className="label" htmlFor="report-period-to">
                To
              </label>
              <input
                id="report-period-to"
                type="date"
                className="input"
                value={to}
                onChange={(event) => onChange({ from, to: event.target.value })}
              />
            </div>
          </div>
        </div>
      )}
    </Popover>
  );
}

/** The row every report page renders: search · Filters · Display options · period. */
export function ReportToolbar({ children }: { children: ReactNode }) {
  return <div className="mb-4 flex flex-wrap items-center gap-2">{children}</div>;
}
